'use client';

import * as React from 'react';
import Image from 'next/image';
import { Play, X, Code, Search, Lock, Loader2 } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { Typography } from '@/components/ui/typography';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';

// Lazy Image Loader Component
function LazyImage({ src, alt, fill, className, ...props }: React.ComponentProps<typeof Image>) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-800">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}
      <Image
        src={src}
        alt={alt}
        fill={fill}
        className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
        loading="lazy"
        {...props}
      />
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-800">
          <span className="text-gray-400 text-sm">Failed to load image</span>
        </div>
      )}
    </>
  );
}

export function VideoSection() {
  const locale = useLocale();
  const t = useTranslations('home.video');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [embedError, setEmbedError] = React.useState(false);
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  const content = {
    pillLabel: t('pill'),
    title: t('title'),
    subtitle: t('subtitle'),
    videoThumbnail: '/images/home/videoBg.webp',
    videoThumbnailAlt: t('videoThumbnailAlt'),
    videoUrl: 'https://youtu.be/6O839zxzwk4',
    statistics: (Array.isArray(t.raw('statistics'))
      ? (t.raw('statistics') as {
        title: string;
        value: string;
        description: string;
        href: string;
      }[])
      : []
    ).map((stat, index) => ({
      ...stat,
      // Preserve existing icon mapping order
      icon: [Code, Search, Lock][index] || Code,
    })),
  };

  const iconColors = [
    { icon: 'text-primary-600', iconDark: 'text-primary-400' },
    { icon: 'text-emerald-600', iconDark: 'text-emerald-400' },
    { icon: 'text-amber-600', iconDark: 'text-amber-400' },
  ];

  const handlePlay = () => {
    setIsModalOpen(true);
    setEmbedError(false);
    // Set a timeout to detect if video is blocked (YouTube shows error after a few seconds)
    setTimeout(() => {
      // Check if iframe loaded - if it's still showing blocked content, we can't detect it
      // but we'll show the fallback button anyway
    }, 3000);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEmbedError(false);
  };

  const handleOpenInYouTube = () => {
    window.open(content.videoUrl, '_blank', 'noopener,noreferrer');
  };

  // Convert YouTube URL to embed format
  // Security: Validates URL and uses youtube-nocookie.com for better privacy (no cookies)
  const getEmbedUrl = (url: string): string => {
    // Extract video ID from various YouTube URL formats
    let videoId = '';

    // Validate URL is from YouTube domain
    if (!url.includes('youtube.com') && !url.includes('youtu.be') && url.includes('http')) {
      console.warn('Invalid YouTube URL:', url);
      return '';
    }

    // Handle youtu.be short URLs: https://youtu.be/VIDEO_ID
    if (url.includes('youtu.be/')) {
      const parts = url.split('youtu.be/')[1];
      videoId = parts?.split('?')[0]?.split('&')[0] || '';
    }
    // Handle youtube.com/watch URLs: https://www.youtube.com/watch?v=VIDEO_ID
    else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v') || '';
    }
    // Handle youtube.com/embed URLs: extract video ID
    else if (url.includes('youtube.com/embed/')) {
      const parts = url.split('youtube.com/embed/')[1];
      videoId = parts?.split('?')[0]?.split('&')[0] || '';
    }
    // If it's already just a video ID (no http)
    else if (!url.includes('http')) {
      // Validate it's alphanumeric (YouTube video IDs are 11 characters alphanumeric)
      if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        videoId = url;
      }
    }

    if (!videoId) {
      console.warn('Could not extract valid video ID from URL:', url);
      return '';
    }

    // Security: Use youtube-nocookie.com for better privacy (no cookies, no tracking)
    // This is the recommended approach for embedding YouTube videos
    return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;
  };

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  return (
    <SectionWrapper
      ref={sectionRef.ref}
      variant="default"
      padding="md"
      className="relative"
    >
      {/* Background accents */}
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute -left-32 top-10 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="absolute right-0 bottom-0 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <SectionHeading
          pill={content.pillLabel}
          title={content.title}
          subtitle={content.subtitle}
          isVisible={sectionRef.isVisible}
        />

        {/* Video Player Section */}
        <div className="mx-auto mb-20">
          <div
            className={`relative rounded-3xl overflow-hidden shadow-2xl  transition-all duration-700 group/video ${sectionRef.isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
              }`}
            style={{ transitionDelay: '200ms' }}
          >
            {/* Glowing border effect */}
            <div className="absolute inset-0 rounded-3xl  transition-all duration-500" />

            <div className="relative aspect-video overflow-hidden">
              <LazyImage
                src={content.videoThumbnail}
                alt="MLM Software Demo Video - Business MLM Platform Overview and Network Marketing Software Features Tutorial"
                fill
                className="object-cover transition-transform duration-700 "
              />

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent" />

              {/* Play Button Overlay */}
              <button
                onClick={handlePlay}
                className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black/40 hover:bg-black/50 transition-all duration-300 group/play"
                aria-label="Play video"
              >
                <div className="relative">
                  {/* Ripple effect rings */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="absolute w-24 h-24 md:w-32 md:h-32 border-2 border-primary-400/80 rounded-full animate-ripple" />
                    <div className="absolute w-24 h-24 md:w-32 md:h-32 border-2 border-primary-400/60 rounded-full animate-ripple" style={{ animationDelay: '0.5s' }} />
                    <div className="absolute w-24 h-24 md:w-32 md:h-32 border-2 border-primary-400/40 rounded-full animate-ripple" style={{ animationDelay: '1s' }} />
                  </div>

                  {/* Play button with smooth scale */}
                  <div className="relative bg-primary-500 hover:bg-primary-700 rounded-full p-5 md:p-6 transition-all duration-500 group-hover/play:scale-125 shadow-2xl border-4 border-white/50 hover:border-white/70 backdrop-blur-sm">
                    <Play className="w-6 h-6 md:w-10 md:h-10 text-white ml-1 transition-all duration-500 group-hover/play:translate-x-1" />
                  </div>

                  {/* Subtle glow on hover */}
                  <div className="absolute inset-0 bg-primary-400/40 rounded-full blur-2xl opacity-0 group-hover/play:opacity-100 transition-opacity duration-500" />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="grid gap-8 md:gap-12 md:grid-cols-3 mx-auto">
          {content.statistics.map((stat, index) => {
            const IconComponent = stat.icon;
            const color = iconColors[index % iconColors.length];
            return (
              <div
                key={index}
                className={`relative text-center md:text-left transition-all duration-500 group ${index < content.statistics.length - 1
                  ? 'border-r border-gray-200 dark:border-gray-800 pr-8 md:pr-12'
                  : ''
                  } ${sectionRef.isVisible
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-10 scale-95'
                  }`}
                style={{ transitionDelay: `${400 + index * 100}ms` }}
              >
                {/* Icon */}
                <div className="mb-6 flex justify-center md:justify-start">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors duration-300">
                      <IconComponent className={`w-8 h-8 transition-colors duration-300 ${color.icon} dark:${color.iconDark}`} />
                    </div>
                  </div>
                </div>

                {/* Title and Value */}
                <Typography
                  variant="h3"
                  className="mb-4 text-gray-900 dark:text-white transition-colors duration-300"
                >
                  {stat.title}{' '}
                  <span className="text-gray-900 dark:text-white">{stat.value}</span>
                </Typography>

                {/* Description */}
                <Typography variant="p" className="leading-relaxed mb-4 text-gray-600 dark:text-gray-400">
                  {stat.description}
                </Typography>

              </div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={handleClose}
        >
          <div
            className="relative w-full max-w-6xl aspect-video bg-black rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all duration-200 hover:scale-110"
              aria-label="Close video"
            >
              <X className="w-6 h-6" />
            </button>

            {/* YouTube Video iframe - Secure implementation */}
            <iframe
              src={getEmbedUrl(content.videoUrl)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              title={content.title}
              frameBorder="0"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      )}
    </SectionWrapper>
  );
}

